import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import TaskFilters from '@/components/v2/molecules/admin/tabs/AdminTodosTab/TaskFilters';
import TaskColumn from '@/components/v2/organisms/admin/tabs/AdminTodosTab/TaskColumn';
import TaskDetailModal from '@/components/v2/organisms/admin/tabs/AdminTodosTab/TaskDetailModal';
import { ChevronLeft, ChevronRight, Filter, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import TaskFormModal from './TaskFormModal';

import { useAuth } from '@/contexts/AuthContext';
import useFilteredTasks from '@/hooks/admin/AdminTodosTabs/useFilteredTasks';
import {
  EnrichedTask,
  useEnrichedTasks,
  useTaskTags,
} from '@/hooks/admin/AdminTodosTabs/useTasks';
import { useProperties } from '@/hooks/useProperties';
import { useUsers } from '@/hooks/useUsers';

import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import TodosTabSkeleton from '@/components/v2/molecules/admin/tabs/AdminTodosTab/TodosTabSkeleton';
import { Role } from '@/constants/rolePermissions';
import { useIsMobile } from '@/hooks/use-mobile';
import getRightRailActions from '@/utils/AdminTodosTab/getRightRailActions';
import {
  formatDaySubtitle,
  formatDayTitle,
  getNextDay,
  getPreviousDay,
  getToday,
  toISODate,
} from '@/utils/shared/dateUtils';
import clsx from 'clsx';
import RightRailToggle from './RightRailToggle';

const AdminTodosTab = () => {
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedTask, setSelectedTask] = useState<EnrichedTask | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [taskFormDefaultDate, setTaskFormDefaultDate] = useState<Date | null>(
    null,
  );

  const [isBacklogOpen, setIsBacklogOpen] = useState(false);

  const toggleBacklog = () => setIsBacklogOpen((v) => !v);

  const { user, profile } = useAuth();

  const isMobile = useIsMobile(1024);

  const {
    data: tasks = [],
    isLoading: isLoadingTasks,
    error: errorTasks,
    refetch: refetchTasks,
  } = useEnrichedTasks();

  const visibleTasks = useMemo(() => {
    if (!user || !profile) return [];

    return tasks.filter((task) => {
      switch (profile.role) {
        case 'super_admin':
          return true;
        case 'property_manager':
          return task.created_by_id === user.id;
        case 'cleaning':
          return task.assigned_user_id === user.id;
        default:
          return false;
      }
    });
  }, [tasks, user, profile]);

  const userRoles = useMemo<Role[]>(() => {
    if (!profile) return [];

    switch (profile.role) {
      case 'super_admin':
        return ['cleaning', 'property_manager'];
      case 'property_manager':
      case 'cleaning':
        return ['cleaning'];
      default:
        return [];
    }
  }, [profile]);

  const { data: users = [] } = useUsers(userRoles);
  const { data: properties = [] } = useProperties();
  const { data: tags = [] } = useTaskTags();

  const {
    filters,
    setFilters,
    clearFilters,
    hasActiveFilters,
    groupedTasks,
    visibleDays,
    today,
    // setVisibleStartDate,
  } = useFilteredTasks(visibleTasks);

  const handleCreateTaskClick = (dueDate: Date | null) => {
    setTaskFormDefaultDate(dueDate);
    setShowTaskForm(true);
  };

  // const handleNextDays = () => setVisibleStartDate((prev) => getNextDay(prev));

  // const handlePrevDays = () =>
  //   setVisibleStartDate((prev) => getPreviousDay(prev));

  const rightRailActions = getRightRailActions({
    isBacklogOpen,
    toggleBacklog,
    showFilters,
    setShowFilters,
    handleCreateTaskClick,
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);

  const todayIndex = useMemo(
    () => visibleDays.findIndex((d) => toISODate(d) === toISODate(today)),
    [visibleDays, today],
  );

  useEffect(() => {
    if (todayIndex < 0 || !scrollRef.current) return;
    setCurrentIndex(todayIndex);

    const todayColumn = scrollRef.current.children[todayIndex] as
      | HTMLElement
      | undefined;
    if (todayColumn) {
      todayColumn.scrollIntoView({
        inline: 'start',
        behavior: 'instant',
        block: 'nearest',
      });
    }
  }, [todayIndex, scrollRef.current]);

  const scrollToIndex = (
    index: number,
    behavior: ScrollBehavior = 'smooth',
  ) => {
    if (!scrollRef.current) return;
    const clamped = Math.max(0, Math.min(index, visibleDays.length - 1));
    const el = scrollRef.current.children[clamped] as HTMLElement | undefined;
    if (el) {
      el.scrollIntoView({ inline: 'start', behavior, block: 'nearest' });
      setCurrentIndex(clamped);
    }
  };

  const handleNextDay = () => scrollToIndex(currentIndex + 1);
  const handlePrevDay = () => scrollToIndex(currentIndex - 1);
  const handleGoToday = () => scrollToIndex(todayIndex);

  useEffect(() => {
    if (!isLoadingTasks && tasks.length > 0 && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  }, [isLoadingTasks, tasks, hasLoadedOnce]);

  if (isLoadingTasks && !hasLoadedOnce) {
    return <TodosTabSkeleton />;
  }

  if (errorTasks) {
    return (
      <TabErrorBanner
        title="Error al cargar tareas"
        message="No se pudieron cargar las tareas. Por favor, intenta nuevamente."
        onRetry={() => refetchTasks()}
      />
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden relative">
      <div className="space-y-3">
        <div className="flex justify-between items-center min-h-10">
          <h2 className="text-2xl font-bold">Tareas</h2>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="relative"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
              {hasActiveFilters && (
                <Badge
                  variant="secondary"
                  className="ml-2 h-5 w-5 p-0 flex items-center justify-center"
                >
                  {Object.values(filters).filter(Boolean).length}
                </Badge>
              )}
            </Button>

            {profile?.role !== 'cleaning' && (
              <Button
                className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
                onClick={() => handleCreateTaskClick(null)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nueva Tarea
              </Button>
            )}
          </div>
        </div>

        {showFilters && (
          <TaskFilters
            clearFilters={clearFilters}
            filters={filters}
            hasActiveFilters={hasActiveFilters}
            properties={properties}
            setFilters={setFilters}
            tags={tags}
            users={users}
          />
        )}
      </div>

      <div className="flex justify-between items-center gap-2 mt-6">
        <Button
          variant="ghost"
          className="bg-white"
          onClick={handlePrevDay}
          disabled={currentIndex <= 0}
        >
          <ChevronLeft /> Día anterior
        </Button>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="bg-white"
            onClick={handleNextDay}
            disabled={currentIndex >= visibleDays.length - 1}
          >
            Día siguiente <ChevronRight />
          </Button>

          <Button
            variant="secondary"
            onClick={handleGoToday}
            disabled={todayIndex === -1 || currentIndex === todayIndex}
          >
            Hoy
          </Button>
        </div>
      </div>

      <div className="relative flex-1 min-h-0 mt-6 pr-14 lg:pr-12 bg-white">
        <RightRailToggle actions={rightRailActions} />

        <div
          className={clsx(
            'grid h-full max-h-full gap-4',
            isMobile
              ? 'grid-cols-1'
              : isBacklogOpen
              ? 'lg:grid-cols-[1fr_340px]'
              : 'lg:grid-cols-[1fr]',
          )}
        >
          {isMobile && isBacklogOpen && (
            <div
              className="overflow-hidden transition-[max-height,opacity,transform] duration-300 ease-out max-h-[80vh] opacity-100 translate-y-0"
              data-state="open"
            >
              <TaskColumn
                title="Backlog"
                subtitle="Sin fecha asignada"
                tasks={groupedTasks.backlog}
                onTaskClick={setSelectedTask}
                onCreateTaskClick={() => handleCreateTaskClick(null)}
                columnType="backlog"
              />
            </div>
          )}

          <div
            ref={scrollRef}
            className="grid grid-cols-1 lg:grid-flow-col lg:auto-cols-[minmax(280px,1fr)] gap-4 h-full min-h-0 max-h-full overflow-x-auto custom-scrollbar"
          >
            {visibleDays.map((day) => {
              const isoKey = toISODate(day);
              const tasks = groupedTasks.byDate?.[isoKey] ?? [];
              return (
                <TaskColumn
                  key={isoKey}
                  title={formatDayTitle(day)}
                  subtitle={formatDaySubtitle(day)}
                  tasks={tasks}
                  onTaskClick={setSelectedTask}
                  onCreateTaskClick={() => handleCreateTaskClick(day)}
                  columnType={isoKey}
                />
              );
            })}
          </div>

          {!isMobile && isBacklogOpen && (
            <div
              className={clsx(
                'h-full',
                'data-[state=open]:animate-in data-[state=open]:slide-in-from-right',
                'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
                'data-[state=closed]:duration-300 data-[state=open]:duration-500',
              )}
              data-state={isBacklogOpen ? 'open' : 'closed'}
            >
              <TaskColumn
                title="Backlog"
                subtitle="Sin fecha asignada"
                tasks={groupedTasks.backlog}
                onTaskClick={setSelectedTask}
                onCreateTaskClick={() => handleCreateTaskClick(null)}
                columnType="backlog"
              />
            </div>
          )}
        </div>
      </div>

      <TaskFormModal
        open={showTaskForm}
        onOpenChange={setShowTaskForm}
        defaultDueDate={taskFormDefaultDate}
      />

      <TaskDetailModal
        task={selectedTask}
        open={!!selectedTask}
        onOpenChange={(open) => !open && setSelectedTask(null)}
      />
    </div>
  );
};

export default AdminTodosTab;
